import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useIsMobile } from "@/hooks/use-mobile";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EventFilterMetadataItem } from "../../slices/eventListSlice";

interface EventFilterSidebarProps {
  filters: {
    searchTerm: string;
    tradeIds: string[];
    eventTypeIds: string[];
    businessRoleIds: string[];
    categoryIds: string[];
    tagIds: string[];
  };
  onFilterChange: (filterKey: string, value: string | string[]) => void;
  eventTypes: EventFilterMetadataItem[];
  eventCategories: EventFilterMetadataItem[];
  businessRoles: EventFilterMetadataItem[];
  trades: EventFilterMetadataItem[];
  eventTags: EventFilterMetadataItem[];
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface FilterSectionProps {
  title: string;
  items: EventFilterMetadataItem[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  showViewMore?: boolean;
}

const FilterSection: React.FC<FilterSectionProps> = ({
  title,
  items,
  selectedIds,
  onToggle,
  showViewMore = true,
}) => {
  const [showAll, setShowAll] = useState(false);
  const visibleItems = showViewMore && !showAll ? items.slice(0, 5) : items;

  if (items.length === 0) return null;

  return (
    <div className="space-y-2">
      <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
        {title}
      </Label>
      <div className="space-y-1">
        {visibleItems.map((item) => {
          const isSelected = selectedIds.includes(String(item.id));

          return (
            <label
              className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 p-1.5 rounded-md transition-colors"
              key={item.id}
            >
              <input
                checked={isSelected}
                className="accent-primary h-4 w-4 rounded"
                onChange={() => onToggle(String(item.id))}
                type="checkbox"
              />
              <span className="flex-1">{item.name}</span>
            </label>
          );
        })}
      </div>
      {showViewMore && items.length > 5 && (
        <button
          className="text-blue-600 dark:text-blue-400 text-sm font-medium hover:underline mt-1"
          onClick={() => setShowAll((prev) => !prev)}
          type="button"
        >
          {showAll ? "View less" : `View more (${items.length - 5})`}
        </button>
      )}
    </div>
  );
};

const EventFilterSidebar: React.FC<EventFilterSidebarProps> = ({
  filters,
  onFilterChange,
  eventTypes,
  eventCategories,
  businessRoles,
  trades,
  eventTags,
  isOpen,
  onOpenChange,
}) => {
  const isMobile = useIsMobile();
  const [searchInput, setSearchInput] = useState(filters.searchTerm);
  const debouncedSearch = useDebouncedValue(searchInput, 500);

  useEffect(() => {
    if (debouncedSearch !== filters.searchTerm) {
      onFilterChange("searchTerm", debouncedSearch);
    }
  }, [debouncedSearch, filters.searchTerm, onFilterChange]);

  const handleToggle = (filterKey: string, id: string, currentIds: string[]) => {
    const newIds = currentIds.includes(id)
      ? currentIds.filter((i) => i !== id)
      : [...currentIds, id];
    onFilterChange(filterKey, newIds);
  };

  const hasActiveFilters =
    filters.searchTerm ||
    filters.eventTypeIds.length > 0 ||
    filters.categoryIds.length > 0 ||
    filters.tradeIds.length > 0 ||
    filters.businessRoleIds.length > 0 ||
    filters.tagIds.length > 0;

  const handleClearAll = () => {
    setSearchInput("");
    onFilterChange("searchTerm", "");
    onFilterChange("eventTypeIds", []);
    onFilterChange("categoryIds", []);
    onFilterChange("tradeIds", []);
    onFilterChange("businessRoleIds", []);
    onFilterChange("tagIds", []);
  };

  const filterContent = (
    <div className="space-y-6">
      {/* Search */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Search
        </Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            className="pl-9"
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search events..."
            value={searchInput}
          />
        </div>
      </div>

      {/* Clear All Button */}
      {hasActiveFilters && (
        <Button
          className="w-full"
          onClick={handleClearAll}
          size="sm"
          variant="outline"
        >
          <X className="h-4 w-4 mr-2" />
          Clear All Filters
        </Button>
      )}

      {/* Event Types */}
      <FilterSection
        items={eventTypes}
        onToggle={(id) => handleToggle("eventTypeIds", id, filters.eventTypeIds)}
        selectedIds={filters.eventTypeIds}
        title="Event Types"
      />

      {/* Categories */}
      <FilterSection
        items={eventCategories}
        onToggle={(id) => handleToggle("categoryIds", id, filters.categoryIds)}
        selectedIds={filters.categoryIds}
        title="Categories"
      />

      {/* Trades */}
      <FilterSection
        items={trades}
        onToggle={(id) => handleToggle("tradeIds", id, filters.tradeIds)}
        selectedIds={filters.tradeIds}
        title="Trades"
      />

      {/* Business Roles */}
      <FilterSection
        items={businessRoles}
        onToggle={(id) => handleToggle("businessRoleIds", id, filters.businessRoleIds)}
        selectedIds={filters.businessRoleIds}
        title="Business Roles"
      />

      {/* Tags */}
      <FilterSection
        items={eventTags}
        onToggle={(id) => handleToggle("tagIds", id, filters.tagIds)}
        selectedIds={filters.tagIds}
        title="Tags"
      />
    </div>
  );

  if (isMobile) {
    return (
      <Sheet onOpenChange={onOpenChange} open={isOpen}>
        <SheetContent
          className="h-screen p-6 overflow-y-auto"
          side="left"
          style={{ width: "100vw", maxWidth: "100vw" }}
        >
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
          </SheetHeader>
          <div className="mt-4">{filterContent}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside className="w-full h-full overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-sm dark:[&::-webkit-scrollbar-thumb]:bg-gray-600">
      <h2 className="text-lg font-semibold mb-1">Filters</h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Refine your event list
      </p>
      {filterContent}
    </aside>
  );
};

export default EventFilterSidebar;
